import React, { Children, useEffect, useRef } from "react";
import {
  Container,
  StackedContainer,
  NavigationButton,
  Pagination,
  PaginationDot,
  SlideCount,
} from "./carousel.full-page.styles";
import { useCarousel } from "../../lib/hooks";

import { AnimatePresence } from "../../styles/base/_allBase.styles";
import { announceAnalyticsInteraction } from "../../lib/analytics";

const Carousel = ({ children, timeOut = 0, ariaLabel = "Image gallery", analyticsModuleId = "carousel" }) => {
  const [imageIndex, , changeImage] = useCarousel(children, timeOut);
  const childArray = React.Children.toArray(children);

  const timeoutRef = useRef(null);
  function resetTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }
  useEffect(() => {
    resetTimeout();
    if (childArray[0].props.cycle) {
      timeoutRef.current = setTimeout(() => changeImage(1), 10000);
    } else {
      return;
    }
    return () => {
      resetTimeout();
    };
  }, [imageIndex]);

  function style(i) {
    if (i === imageIndex) {
      return { opacity: 1 };
    } else return { opacity: 0 };
  }

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      announceAnalyticsInteraction("carousel_used", {
        module_id: analyticsModuleId,
        interaction_type: "keyboard",
        carousel_direction: "previous",
      });
      changeImage("prev");
    }
    if (event.key === "ArrowRight") {
      announceAnalyticsInteraction("carousel_used", {
        module_id: analyticsModuleId,
        interaction_type: "keyboard",
        carousel_direction: "next",
      });
      changeImage("next");
    }
  };

  const selectImage = (value, interactionType = "click") => {
    announceAnalyticsInteraction("carousel_used", {
      module_id: analyticsModuleId,
      interaction_type: interactionType,
      carousel_direction: typeof value === "string" ? value : "direct",
      carousel_item_position: typeof value === "number" ? value + 1 : undefined,
    });
    changeImage(value);
  };

  return (
    <Container
      name={"carousel container"}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence initial={false} mode="wait">
        {Children.map(children, (child, i) => (
          <StackedContainer style={style(i)} key={i}>
            {React.cloneElement(child, { ...child.props })}
          </StackedContainer>
        ))}
      </AnimatePresence>
      <NavigationButton
        type="button"
        $side="left"
        aria-label="Show previous image"
        onClick={() => selectImage("prev")}
      >
        <span aria-hidden="true">‹</span>
      </NavigationButton>
      <NavigationButton
        type="button"
        $side="right"
        aria-label="Show next image"
        onClick={() => selectImage("next")}
      >
        <span aria-hidden="true">›</span>
      </NavigationButton>
      <Pagination aria-label="Choose gallery image">
        {childArray.map((_, index) => (
          <PaginationDot
            key={index}
            type="button"
            $active={index === imageIndex}
            aria-label={`Show image ${index + 1} of ${childArray.length}`}
            aria-current={index === imageIndex ? "true" : undefined}
            onClick={() => selectImage(index)}
          />
        ))}
      </Pagination>
      <SlideCount aria-live="polite">
        {imageIndex + 1} / {childArray.length}
      </SlideCount>
    </Container>
  );
};

export { Carousel };
